/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { ArrowLeft, Plus, Upload, Send, CheckCircle2, MessageSquareWarning, Lock, History } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/apiClient";
import { useMatch } from "../lib/MatchContext";
import RequireMatch from "../components/RequireMatch";
import SortableReportItem from "../components/report/SortableReportItem";
import ReportItemEditor from "../components/report/ReportItemEditor";
import { DEPT_STATUS, LAYOUTS, errMsg } from "../lib/reportApi";
import { fmtDateTimeIST } from "../lib/formatDateTime";

function ReviewBar({ data, onSubmit, onReview }) {
  const [note, setNote] = useState("");
  const [asking, setAsking] = useState(false);
  return (
    <div className="mc-panel p-4 mb-4 flex flex-col md:flex-row md:items-center gap-3" data-testid="report-review-bar">
      {data.review_note && (
        <div className="flex-1 text-sm text-warnAmber flex items-start gap-2" data-testid="report-review-note">
          <MessageSquareWarning className="w-4 h-4 mt-0.5 shrink-0" /> Corrections requested: {data.review_note}
        </div>
      )}
      {!data.review_note && <div className="flex-1 text-sm text-zinc-500">{data.items.filter((i) => i.approved).length} of {data.items.length} items approved for the final report.</div>}
      {data.can_edit && data.status !== "submitted" && data.status !== "approved" && (
        <button onClick={onSubmit} className="bg-sky-600 text-white px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-1" data-testid="report-submit-btn">
          <Send className="w-3 h-3" /> Submit for review
        </button>
      )}
      {data.can_approve && !data.locked && (
        <div className="flex flex-wrap items-center gap-2">
          {asking ? (
            <>
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What needs to change?" className="border border-cmdBorder p-2 text-sm w-64" data-testid="report-changes-note" />
              <button onClick={() => { onReview("request_changes", note); setAsking(false); setNote(""); }} disabled={!note.trim()} className="border border-warnAmber text-warnAmber px-3 py-2 text-xs font-bold uppercase tracking-widest disabled:opacity-40" data-testid="report-changes-send">Send</button>
              <button onClick={() => setAsking(false)} className="text-xs uppercase tracking-widest text-zinc-500 px-2">Cancel</button>
            </>
          ) : (
            <button onClick={() => setAsking(true)} className="border border-warnAmber text-warnAmber px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-1" data-testid="report-request-changes-btn">
              <MessageSquareWarning className="w-3 h-3" /> Request corrections
            </button>
          )}
          <button onClick={() => onReview("approve")} disabled={!data.items.length} className="bg-readyGreen text-white px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-1 disabled:opacity-40" data-testid="report-approve-btn">
            <CheckCircle2 className="w-3 h-3" /> Approve department
          </button>
        </div>
      )}
    </div>
  );
}

export default function ReportDepartmentPage() {
  const { dept: rawDept } = useParams();
  const dept = decodeURIComponent(rawDept || "");
  const [params, setParams] = useSearchParams();
  const { selectedMatchId: M } = useMatch();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [bulkLayout, setBulkLayout] = useState(1);
  const [uploading, setUploading] = useState(false);
  const bulkRef = useRef(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const base = `/matches/${M}/report/departments/${encodeURIComponent(dept)}`;
  const load = async () => {
    const { data: d } = await api.get(`${base}/items`);
    setData(d);
    return d;
  };
  useEffect(() => {
    if (!M) return;
    load().then((d) => {
      const want = params.get("item");
      if (want) setEditing(d.items.find((i) => i.id === want) || null);
    });
  }, [M, dept]);

  if (!M) return <RequireMatch />;
  if (!data) return <div className="p-8 text-zinc-500 font-mono-live">loading…</div>;

  const st = DEPT_STATUS[data.status] || DEPT_STATUS.not_started;
  const setItem = (it) => setData((d) => ({ ...d, items: d.items.map((x) => (x.id === it.id ? it : x)) }));

  const addItem = async () => {
    try {
      const { data: it } = await api.post(`${base}/items`, { heading: "", description: "", layout: bulkLayout });
      setData((d) => ({ ...d, items: [...d.items, it] }));
      setEditing(it);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const bulk = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f, f.name));
    try {
      const { data: r } = await api.post(`${base}/bulk-upload?layout=${bulkLayout}`, fd);
      toast.success(`${r.created.length} photo(s) added — now add headings & descriptions`);
      if (r.failed.length) toast.error(`Could not read: ${r.failed.join(", ")}`);
      load();
    } catch (e) { toast.error(errMsg(e, "Bulk upload failed")); } finally { setUploading(false); }
  };

  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = data.items.map((i) => i.id);
    const next = arrayMove(data.items, ids.indexOf(active.id), ids.indexOf(over.id));
    setData((d) => ({ ...d, items: next }));
    try { await api.put(`${base}/order`, { ids: next.map((i) => i.id) }); toast.success("Order saved", { duration: 1200 }); }
    catch (e) { toast.error(errMsg(e, "Could not save order")); load(); }
  };

  const act = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="p-6 md:p-10 pb-24" data-testid="page-report-department">
      <Link to="/post-event-report" className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-4" data-testid="report-back-link"><ArrowLeft className="w-3 h-3" /> Post Event Report</Link>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 border-b border-cmdBorder pb-6">
        <div>
          <div className="mc-label">Department Report</div>
          <h1 className="font-head font-black text-4xl md:text-5xl uppercase tracking-tight leading-none mt-2" data-testid="report-dept-title">{dept}</h1>
          <div className="flex items-center gap-2 mt-3">
            <span className={`border px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold ${st.cls}`} data-testid="report-dept-status">{st.label}</span>
            {data.locked && <span className="border border-incidentRed text-incidentRed px-2 py-0.5 text-[10px] uppercase tracking-widest flex items-center gap-1"><Lock className="w-3 h-3" /> Locked</span>}
            <span className="text-xs text-zinc-500">{data.items.length} item(s)</span>
          </div>
        </div>
        {data.can_edit && (
          <div className="flex flex-wrap items-center gap-2">
            <select value={bulkLayout} onChange={(e) => setBulkLayout(Number(e.target.value))} className="border border-cmdBorder p-2 text-xs" data-testid="report-default-layout" title="Layout for new items">
              {LAYOUTS.map((l) => <option key={l.value} value={l.value}>New items: {l.label}</option>)}
            </select>
            <input ref={bulkRef} type="file" multiple accept="image/*,.heic,.heif" className="hidden" onChange={(e) => { bulk(e.target.files); e.target.value = ""; }} data-testid="report-bulk-input" />
            <button onClick={() => bulkRef.current?.click()} disabled={uploading} className="border border-cmdBorder px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-1 disabled:opacity-50" data-testid="report-bulk-btn">
              <Upload className="w-3 h-3" /> {uploading ? "Uploading…" : "Bulk upload photos"}
            </button>
            <button onClick={addItem} className="bg-black text-white px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-1" data-testid="report-add-item-btn">
              <Plus className="w-3 h-3" /> Add item
            </button>
          </div>
        )}
      </div>

      <ReviewBar data={data}
        onSubmit={() => act(() => api.post(`${base}/submit`), "Submitted for review")}
        onReview={(action, note = "") => act(() => api.post(`${base}/review`, { action, note }), action === "approve" ? "Department approved" : "Corrections requested")} />

      {data.can_edit && (
        <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); bulk(e.dataTransfer?.files); }}
          className="border-2 border-dashed border-cmdBorder p-4 mb-4 text-center text-xs text-zinc-500" data-testid="report-bulk-dropzone">
          Drop several photos here to create one item per photo · drag the handle on a row to change its order
        </div>
      )}

      {data.items.length === 0 ? (
        <div className="mc-panel p-10 text-center text-zinc-500 text-sm" data-testid="report-empty">No report items yet.{data.can_edit ? " Add an item or bulk-upload photos to begin." : ""}</div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={data.items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <ul className="space-y-2" data-testid="report-item-list">
              {data.items.map((it, i) => (
                <SortableReportItem key={it.id} item={it} index={i} canEdit={data.can_edit} canApprove={data.can_approve && !data.locked}
                  onOpen={() => { setEditing(it); setParams({ item: it.id }, { replace: true }); }}
                  onDuplicate={() => act(() => api.post(`/report-items/${it.id}/duplicate`), "Item duplicated")}
                  onDelete={() => window.confirm(`Delete “${it.heading || "Untitled"}”?`) && act(() => api.delete(`/report-items/${it.id}`), "Item deleted")}
                  onToggleApproval={() => act(() => api.patch(`/report-items/${it.id}/approval`, { approved: !it.approved }))} />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {!!data.history?.length && (
        <details className="mt-8 mc-panel p-4 text-xs" data-testid="report-dept-history">
          <summary className="mc-label cursor-pointer flex items-center gap-1"><History className="w-3 h-3" /> Department activity</summary>
          <ul className="mt-3 space-y-1">
            {data.history.slice().reverse().map((h, i) => <li key={i} className="text-zinc-500"><span className="text-zinc-800">{h.by}</span> {h.action} · {fmtDateTimeIST(h.at)}</li>)}
          </ul>
        </details>
      )}

      <ReportItemEditor item={editing} open={!!editing} canEdit={data.can_edit}
        onClose={() => { setEditing(null); setParams({}, { replace: true }); load(); }} onChanged={setItem} />
    </div>
  );
}
